import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { AlertCircle, Clock } from "lucide-react";
import { TEMPLATE_IDS, CheckInTemplateType, getTemplateTypeFromId } from "@/constants/checkInTemplates";
import { getTemplateConfig } from "@/constants/reportTemplateConfigurations";

const GET_BLOCKER_RESPONSES = gql`
  query GetBlockerResponses($reportId: ID!, $first: Int, $after: String) {
    listResponses(reportId: $reportId, first: $first, after: $after) {
      edges {
        node {
          id
          employee {
            id
            name
            avatarUrl
          }
          answers {
            id
            questionId
            answerText
            extractedData
            question {
              id
              header
            }
          }
        }
      }
    }
  }
`;

interface BlockerData {
  blocker_name: string;
  blocker_description: string;
  blocker_help_needed: string;
  blocker_discuss_today: boolean;
}

interface BlockerResponse {
  id: string;
  employee: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  answers: Array<{
    id: string;
    questionId: string;
    answerText: string;
    extractedData: any;
    question: {
      id: string;
      header: string;
    };
  }>;
}

interface ProcessedBlocker {
  name: string;
  description: string;
  helpNeeded: string;
  needsToBeDiscussed: boolean;
  reportedBy: string;
  reportedByAvatar?: string;
  id: string;
}

interface BlockerCardProps {
  blocker: ProcessedBlocker;
  isUrgent: boolean;
}

function BlockerCard({ blocker, isUrgent }: BlockerCardProps) {
  return (
    <div className={cn(
      "rounded-lg border p-4 space-y-3",
      isUrgent && "border-l-4 border-l-destructive"
    )}>
      <div className="flex items-start justify-between gap-4">
        <h4 className="font-medium text-base">{blocker.name}</h4>
        <div className="flex items-center gap-2 shrink-0">
          <Avatar className="h-7 w-7">
            <AvatarImage src={blocker.reportedByAvatar} alt={blocker.reportedBy} />
            <AvatarFallback>{blocker.reportedBy.split(' ').map(n => n[0]).join('')}</AvatarFallback>
          </Avatar>
          <span className="text-sm text-muted-foreground">{blocker.reportedBy}</span>
        </div>
      </div>
      
      <p className="text-sm text-muted-foreground">{blocker.description}</p>
      
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="flex items-center gap-1">
          {isUrgent ? (
            <AlertCircle className="h-3.5 w-3.5 text-destructive" />
          ) : (
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <span>{isUrgent ? 'Urgent' : 'Non-urgent'}</span>
        </Badge>
        <Badge variant="secondary">
          Needs help from: {blocker.helpNeeded}
        </Badge>
      </div>
    </div>
  );
}

interface BlockerSummaryProps {
  reportId: string;
  templateId?: string;
  title?: string;
}

// Fix blockerQuestionIds typing
type BlockerQuestionIdsMap = {
  [key in CheckInTemplateType]: string[];
};

const blockerQuestionIds: BlockerQuestionIdsMap = {
  [CheckInTemplateType.STANDUP]: [
    "33333333-3333-3333-3333-333333333303",
    "11111111-1111-1111-1111-111111111103"
  ],
  [CheckInTemplateType.WEEKLY_CHECKIN]: [],
  [CheckInTemplateType.DAILY_STANDUP]: ["11111111-1111-1111-1111-111111111103"],
  [CheckInTemplateType.RETROSPECTIVE]: [],
  [CheckInTemplateType.ONE_ON_ONE]: [],
  [CheckInTemplateType.FEEDBACK]: [],
  [CheckInTemplateType.CUSTOM]: [
    "33333333-3333-3333-3333-333333333303",
    "11111111-1111-1111-1111-111111111103",
    // Add any other known blocker question IDs here
  ]
};

export function BlockerSummary({ reportId, templateId, title }: BlockerSummaryProps) {
  // Get the template type from the ID
  const templateType = templateId ? getTemplateTypeFromId(templateId) : CheckInTemplateType.CUSTOM;
  const isStandupTemplate = templateId === TEMPLATE_IDS.STANDUP;
  const isDailyStandupTemplate = templateId === TEMPLATE_IDS.DAILY_STANDUP;
  const isWeeklyCheckinTemplate = templateId === TEMPLATE_IDS.WEEKLY_CHECKIN;
  
  // Get template configuration
  const templateConfig = templateId ? getTemplateConfig(templateId) : null;
  
  // Use type assertion for indexing
  const relevantQuestionIds = blockerQuestionIds[templateType as keyof typeof blockerQuestionIds] || blockerQuestionIds[CheckInTemplateType.CUSTOM];
  
  // Set the title based on template configuration or fall back to defaults
  let titleToUse = title || templateConfig?.blockerSummaryLabel || 'Blocker Summary';
  if (isStandupTemplate && !templateConfig?.blockerSummaryLabel) {
    titleToUse = 'Critical Issues';
  } else if (isDailyStandupTemplate && !templateConfig?.blockerSummaryLabel) {
    titleToUse = 'Blocker Summary';
  } else if (isWeeklyCheckinTemplate && !templateConfig?.blockerSummaryLabel) {
    titleToUse = 'Challenges';
  }

  // Debug info for development
  console.log('[DEBUG] BlockerSummary: Initializing with reportId:', reportId);
  console.log('[DEBUG] BlockerSummary: Template details:', {
    templateId,
    templateType,
    isStandupTemplate,
    isDailyStandupTemplate,
    isWeeklyCheckinTemplate,
    relevantQuestionIds
  });

  const { data, loading, error } = useQuery(GET_BLOCKER_RESPONSES, {
    variables: { 
      reportId,
      first: 100
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    onError: (error) => {
      console.error('[ERROR] BlockerSummary: GraphQL error:', error);
      console.error('[ERROR] BlockerSummary: GraphQL error details:', {
        message: error.message,
        networkError: error.networkError,
        graphQLErrors: error.graphQLErrors,
      });
    }
  });

  // Debug state
  console.log('[DEBUG] BlockerSummary: Current state:', {
      loading,
      error: error ? {
        message: error.message,
        networkError: error.networkError,
        graphQLErrors: error.graphQLErrors,
      } : null,
      hasData: !!data,
      responseCount: data?.listResponses?.edges?.length ?? 0
    });

  if (loading) {
    console.log('[DEBUG] BlockerSummary: Loading state');
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">{titleToUse}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    console.error('[ERROR] BlockerSummary: Error rendering:', error);
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">{titleToUse}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">
            Error loading blockers. Please try refreshing the page.
          </p>
            <pre className="mt-2 text-xs text-destructive whitespace-pre-wrap">
              {JSON.stringify(error, null, 2)}
            </pre>
        </CardContent>
      </Card>
    );
  }

  if (!data?.listResponses?.edges?.length) {
    console.log('[DEBUG] BlockerSummary: No responses found');
    return null;
  }

  try {
    // Process responses to extract blockers
    const blockers = data.listResponses.edges
      .map(({ node }: { node: BlockerResponse }) => {
        console.log('[DEBUG] BlockerSummary: Processing response:', {
            responseId: node.id,
            employeeName: node.employee.name,
          totalAnswers: node.answers.length,
          answerQuestionIds: node.answers.map(a => a.questionId)
          });

        const blockerAnswers = node.answers.filter(answer => {
          const isBlockerQuestion = relevantQuestionIds.includes(answer.questionId);
          
          console.log('[DEBUG] BlockerSummary: Checking answer:', {
              questionId: answer.questionId,
              isBlockerQuestion,
            matchesAnyRelevantId: relevantQuestionIds.includes(answer.questionId),
            hasExtractedData: !!answer.extractedData,
            header: answer.question?.header
            });
          
          return isBlockerQuestion;
        });

        if (blockerAnswers.length > 0) {
          console.log('[DEBUG] BlockerSummary: Found blocker answers:', blockerAnswers.length);
        }

        return blockerAnswers.flatMap(answer => {
          try {
            // MAIN CHANGE: Use extractedData directly instead of parsing answerText
            const extractedData = answer.extractedData;
            
            console.log('[DEBUG] BlockerSummary: Extracted data:', {
              hasExtractedData: !!extractedData,
              extractedDataType: typeof extractedData,
              extractedData: extractedData
            });

            // If no extracted data, try to parse answerText as fallback
            const blockersData = extractedData || 
                (answer.answerText ? JSON.parse(answer.answerText) : null);
            
            if (!blockersData) {
              console.log('[DEBUG] BlockerSummary: No blocker data found');
              return [];
            }

            if (!blockersData.has_blocker || !blockersData.blockers?.length) {
              console.log('[DEBUG] BlockerSummary: No blockers in data');
              return [];
            }

            console.log('[DEBUG] BlockerSummary: Processing blockers:', blockersData.blockers.length);
            
            return blockersData.blockers.map((blocker: BlockerData) => ({
              name: blocker.blocker_name,
              description: blocker.blocker_description,
              helpNeeded: blocker.blocker_help_needed,
              needsToBeDiscussed: blocker.blocker_discuss_today,
              reportedBy: node.employee.name,
              reportedByAvatar: node.employee.avatarUrl,
              id: `${node.id}-${blocker.blocker_name}`
            }));
          } catch (error) {
            console.error('[ERROR] BlockerSummary: Error processing answer:', error);
            console.error('[ERROR] BlockerSummary: Raw data:', {
              extractedData: answer.extractedData,
              answerText: answer.answerText
            });
            return [];
          }
        });
      })
      .flat()
      .filter((blocker: unknown): blocker is ProcessedBlocker => {
        const isValid = blocker && typeof blocker === 'object' && 'name' in blocker && 'description' in blocker;
        if (!isValid) {
          console.warn('[WARN] BlockerSummary: Filtered out invalid blocker:', blocker);
        }
        return Boolean(isValid);
      });

    // Deduplicate blockers based on ID
    const uniqueBlockers = Array.from(
      new Map(blockers.map((blocker: ProcessedBlocker) => [blocker.id, blocker])).values()
    ) as ProcessedBlocker[];

    console.log('[DEBUG] BlockerSummary: Processed blockers:', uniqueBlockers.length);

    const urgentBlockers = uniqueBlockers.filter((blocker) => blocker.needsToBeDiscussed);
    const nonUrgentBlockers = uniqueBlockers.filter((blocker) => !blocker.needsToBeDiscussed);

    // Get unique teams needed for resolution
    const teamsNeeded = Array.from(new Set(blockers.map((b: ProcessedBlocker) => b.helpNeeded))).sort() as string[];

    console.log('[DEBUG] BlockerSummary: Categorized blockers:', {
        urgent: urgentBlockers.length,
      nonUrgent: nonUrgentBlockers.length,
      teamsNeeded: teamsNeeded
      });

    if (!urgentBlockers.length && !nonUrgentBlockers.length) {
      console.log('[DEBUG] BlockerSummary: No blockers found after processing');
      return null;
    }

    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-destructive">{titleToUse}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {urgentBlockers.length > 0 && (
            <div className="space-y-3">
              <div className="space-y-3">
                {urgentBlockers.map((blocker) => (
                  <BlockerCard 
                    key={`${blocker.name}-${blocker.reportedBy}`} 
                    blocker={blocker}
                    isUrgent={true}
                  />
                ))}
              </div>
            </div>
          )}

          {nonUrgentBlockers.length > 0 && (
            <div className="space-y-3">
              <div className="space-y-3">
                {nonUrgentBlockers.map((blocker) => (
                  <BlockerCard 
                    key={`${blocker.name}-${blocker.reportedBy}`} 
                    blocker={blocker}
                    isUrgent={false}
                  />
                ))}
              </div>
            </div>
          )}

          {teamsNeeded.length > 0 && (
            <div className="pt-4 border-t">
              <h3 className="font-semibold text-sm mb-3">Required for Resolution</h3>
              <div className="flex flex-wrap gap-2">
                {teamsNeeded.map((team) => (
                  <Badge key={team} variant="outline" className="mr-1 mb-1">
                    {team}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  } catch (error) {
    console.error('[ERROR] BlockerSummary: Error processing data:', error);
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">{titleToUse}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">
            Error processing blockers. Please try refreshing the page.
          </p>
            <pre className="mt-2 text-xs text-destructive whitespace-pre-wrap">
              {error instanceof Error ? error.message : String(error)}
            </pre>
        </CardContent>
      </Card>
    );
  }
} 